import React, { useState, useEffect, useCallback, useRef } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';
import './TemplatesManagerTab.css';

const CATEGORIES = [
  'all',
  'cold_outreach',
  'follow_up',
  'meeting_request',
  'product_demo',
  'objection_handling',
  'referral',
  'general',
];

const MERGE_TAGS = [
  { tag: '{{firstName}}', desc: 'Prospect First Name' },
  { tag: '{{lastName}}', desc: 'Prospect Last Name' },
  { tag: '{{companyName}}', desc: 'Company Name' },
  { tag: '{{industry}}', desc: 'Industry' },
  { tag: '{{jobTitle}}', desc: 'Job Title' },
  { tag: '{{senderName}}', desc: 'Sender Name' },
  { tag: '{{meetingUrl}}', desc: 'Meeting Booking Link' },
];

function TemplatesManagerTab({ canManage, onUseInSequence }) {
  const [templates, setTemplates] = useState([]);
  const [complianceProfile, setComplianceProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [error, setError] = useState(null);
  const [successToast, setSuccessToast] = useState(null);

  // Modals state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [actionInProgressId, setActionInProgressId] = useState(null);

  // Form state for Template Editor
  const [formData, setFormData] = useState({
    name: '',
    category: 'cold_outreach',
    subjectTemplate: '',
    bodyTemplate: '',
    inheritComplianceFooter: true,
    customDisclaimer: '',
  });

  // Track focused field for merge tag insertion
  const focusedFieldRef = useRef('body');

  // Form state for Compliance Profile
  const [compData, setCompData] = useState({
    companyLegalName: '',
    physicalAddress: '',
    registrationNumber: '',
    jurisdiction: 'MY',
    unsubscribeNotice: '',
    regulatedDisclaimer: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tmplRes, compRes] = await Promise.allSettled([
        emailOutreachApi.listTemplates({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
        }),
        emailOutreachApi.getComplianceProfile(),
      ]);

      if (tmplRes.status === 'fulfilled' && tmplRes.value?.success) {
        setTemplates(tmplRes.value.data || []);
      }
      if (compRes.status === 'fulfilled' && compRes.value?.success) {
        setComplianceProfile(compRes.value.data || null);
        if (compRes.value.data) {
          setCompData({
            companyLegalName: compRes.value.data.company_legal_name || '',
            physicalAddress: compRes.value.data.physical_address || '',
            registrationNumber: compRes.value.data.registration_number || '',
            jurisdiction: compRes.value.data.jurisdiction || 'MY',
            unsubscribeNotice: compRes.value.data.unsubscribe_notice || '',
            regulatedDisclaimer: compRes.value.data.regulated_disclaimer || '',
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle open editor
  const handleOpenEditor = (tmpl = null) => {
    if (tmpl) {
      setEditingTemplate(tmpl);
      setFormData({
        name: tmpl.name,
        category: tmpl.category,
        subjectTemplate: tmpl.subject_template,
        bodyTemplate: tmpl.body_template,
        inheritComplianceFooter: tmpl.inherit_compliance_footer !== false,
        customDisclaimer: tmpl.custom_disclaimer || '',
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        category: 'cold_outreach',
        subjectTemplate: '',
        bodyTemplate: '',
        inheritComplianceFooter: true,
        customDisclaimer: '',
      });
    }
    setEditorOpen(true);
  };

  // Insert merge tag into subject or body
  const insertMergeTag = (tag) => {
    if (focusedFieldRef.current === 'subject') {
      setFormData((prev) => ({
        ...prev,
        subjectTemplate: prev.subjectTemplate + tag,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        bodyTemplate: prev.bodyTemplate + tag,
      }));
    }
  };

  // Save template
  const handleSaveTemplate = async (submitForReview = false) => {
    if (
      !formData.name.trim() ||
      !formData.subjectTemplate.trim() ||
      !formData.bodyTemplate.trim()
    ) {
      setError('Please fill in Template Name, Subject, and Body');
      return;
    }

    try {
      setActionInProgressId('saving');
      setError(null);
      let res;
      if (editingTemplate) {
        res = await emailOutreachApi.updateTemplate(editingTemplate.id, formData);
        if (submitForReview && res.success) {
          await emailOutreachApi.submitTemplateForApproval(editingTemplate.id);
        }
      } else {
        res = await emailOutreachApi.createTemplate(formData);
        if (submitForReview && res.success && res.data?.id) {
          await emailOutreachApi.submitTemplateForApproval(res.data.id);
        }
      }

      setEditorOpen(false);
      setSuccessToast(
        submitForReview
          ? 'Template submitted for administrator review!'
          : 'Template saved successfully!'
      );
      setTimeout(() => setSuccessToast(null), 5000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save template');
    } finally {
      setActionInProgressId(null);
    }
  };

  // Submit template for review from card
  const handleSubmitReview = async (id) => {
    try {
      setActionInProgressId(id);
      await emailOutreachApi.submitTemplateForApproval(id);
      setSuccessToast('Template submitted for review');
      setTimeout(() => setSuccessToast(null), 4000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit template');
    } finally {
      setActionInProgressId(null);
    }
  };

  // Approve template
  const handleApprove = async (id) => {
    try {
      setActionInProgressId(id);
      await emailOutreachApi.approveTemplate(id);
      setSuccessToast('Template approved for production use!');
      setTimeout(() => setSuccessToast(null), 4000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to approve template');
    } finally {
      setActionInProgressId(null);
    }
  };

  // Reject template
  const handleReject = async (id) => {
    const reason = window.prompt('Please enter the compliance rejection reason:');
    if (!reason || !reason.trim()) return;

    try {
      setActionInProgressId(id);
      await emailOutreachApi.rejectTemplate(id, reason.trim());
      setSuccessToast('Template rejected and returned to author.');
      setTimeout(() => setSuccessToast(null), 4000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reject template');
    } finally {
      setActionInProgressId(null);
    }
  };

  // Delete or archive template
  const handleDelete = async (template) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete template "${template.name}"?\n\nIf it is linked to sequence steps, it will be safely archived to protect ongoing sequences.`
    );
    if (!confirmed) return;

    try {
      setActionInProgressId(template.id);
      setError(null);
      const res = await emailOutreachApi.deleteTemplate(template.id);
      setSuccessToast(res.data?.message || res.message || 'Template removed successfully');
      setTimeout(() => setSuccessToast(null), 5000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete template');
    } finally {
      setActionInProgressId(null);
    }
  };

  // Save compliance profile
  const handleSaveCompliance = async () => {
    try {
      setActionInProgressId('comp');
      setError(null);
      await emailOutreachApi.updateComplianceProfile(compData);
      setComplianceModalOpen(false);
      setSuccessToast('Workspace compliance profile updated!');
      setTimeout(() => setSuccessToast(null), 5000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update compliance profile');
    } finally {
      setActionInProgressId(null);
    }
  };

  return (
    <div className="eo-tab-content">
      {/* Toast Alert */}
      {successToast && (
        <div className="eo-toast-banner success">
          <div className="eo-toast-body">
            <i className="fas fa-check-circle"></i>
            <span>{successToast}</span>
          </div>
          <button className="eo-toast-close" onClick={() => setSuccessToast(null)}>
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="eo-alert eo-alert-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="eo-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {/* Workspace Compliance Data Template Card */}
      <div className="eo-compliance-card">
        <div className="eo-compliance-info">
          <div className="eo-compliance-icon">
            <i className="fas fa-shield-alt"></i>
          </div>
          <div className="eo-compliance-details">
            <h4>Workspace Legal & Compliance Profile</h4>
            <div className="eo-compliance-meta-strip">
              <span>
                <strong>Entity:</strong> {complianceProfile?.company_legal_name || 'AI LeadGen'}
              </span>
              <span>
                <strong>Address:</strong>{' '}
                {complianceProfile?.physical_address || 'Kuala Lumpur, MY'}
              </span>
              <span>
                <strong>Jurisdiction:</strong> {complianceProfile?.jurisdiction || 'MY'}
              </span>
              {complianceProfile?.registration_number && (
                <span>
                  <strong>Reg No:</strong> {complianceProfile.registration_number}
                </span>
              )}
            </div>
          </div>
        </div>
        {canManage && (
          <button
            className="eo-btn eo-btn-sm eo-btn-secondary"
            onClick={() => setComplianceModalOpen(true)}
          >
            <i className="fas fa-pen"></i> Edit Compliance Data
          </button>
        )}
      </div>

      {/* Header & Filter Controls */}
      <div className="eo-tab-header">
        <div>
          <h3 className="eo-section-title">Governed Email Templates (FR-M07-008)</h3>
          <p className="eo-section-desc">
            Version-controlled outreach templates with multi-tier compliance approvals, mandatory
            legal footers, and dynamic data merge tokens.
          </p>
        </div>
        <div className="eo-filter-bar">
          <select
            className="eo-select eo-select-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Active Templates</option>
            <option value="approved">Approved</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="draft">Draft</option>
            <option value="rejected">Rejected</option>
            <option value="archived">Archived</option>
          </select>

          <select
            className="eo-select eo-select-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat.replace(/_/g, ' ').toUpperCase()}
              </option>
            ))}
          </select>

          {canManage && (
            <button className="eo-btn eo-btn-sm eo-btn-primary" onClick={() => handleOpenEditor()}>
              <i className="fas fa-plus"></i> New Template
            </button>
          )}
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="eo-loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading governed templates...</span>
        </div>
      ) : templates.length === 0 ? (
        <div className="eo-empty-state">
          <div className="eo-empty-icon">
            <i className="fas fa-file-contract"></i>
          </div>
          <h4>No Templates Found</h4>
          <p>Create and submit email templates for compliance approval.</p>
        </div>
      ) : (
        <div className="eo-template-grid">
          {templates.map((tmpl) => (
            <div key={tmpl.id} className="eo-template-card">
              <div>
                <div className="eo-template-header">
                  <div className="eo-template-title-area">
                    <h4>{tmpl.name}</h4>
                    <div className="eo-template-tags-row">
                      <span className={`eo-badge-status ${tmpl.status}`}>
                        {tmpl.status === 'approved' && <i className="fas fa-check-circle"></i>}
                        {tmpl.status === 'pending_approval' && <i className="fas fa-clock"></i>}
                        {tmpl.status.replace(/_/g, ' ')}
                      </span>
                      <span className="eo-badge-version">v{tmpl.version}</span>
                      <span className="eo-pill-neutral">{tmpl.category.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>

                <div className="eo-template-subject-preview">
                  <i
                    className="fas fa-envelope-open-text"
                    style={{ marginRight: '6px', color: '#64748b' }}
                  ></i>
                  {tmpl.subject_template}
                </div>

                <p className="eo-template-body-preview">{tmpl.body_template}</p>

                {tmpl.inherit_compliance_footer && (
                  <div className="eo-template-footer-indicator">
                    <i className="fas fa-shield-alt"></i>
                    <span>Includes Workspace Legal Footer</span>
                  </div>
                )}
              </div>

              <div className="eo-template-actions-row">
                <button
                  className="eo-btn eo-btn-xs eo-btn-secondary"
                  onClick={() => setPreviewTemplate(tmpl)}
                >
                  <i className="fas fa-eye"></i> Preview
                </button>

                <div style={{ display: 'flex', gap: '6px' }}>
                  {canManage && tmpl.status === 'pending_approval' && (
                    <>
                      <button
                        className="eo-btn eo-btn-xs eo-btn-primary"
                        onClick={() => handleApprove(tmpl.id)}
                        disabled={actionInProgressId === tmpl.id}
                        title="Approve for production sequences and outreach"
                      >
                        <i className="fas fa-check"></i> Approve
                      </button>
                      <button
                        className="eo-btn eo-btn-xs eo-btn-danger"
                        onClick={() => handleReject(tmpl.id)}
                        disabled={actionInProgressId === tmpl.id}
                        title="Reject template"
                      >
                        <i className="fas fa-times"></i> Reject
                      </button>
                    </>
                  )}

                  {canManage && (tmpl.status === 'draft' || tmpl.status === 'rejected') && (
                    <button
                      className="eo-btn eo-btn-xs eo-btn-primary"
                      onClick={() => handleSubmitReview(tmpl.id)}
                      disabled={actionInProgressId === tmpl.id}
                    >
                      <i className="fas fa-paper-plane"></i> Submit
                    </button>
                  )}

                  {canManage && (
                    <button
                      className="eo-btn eo-btn-xs eo-btn-secondary"
                      onClick={() => handleOpenEditor(tmpl)}
                    >
                      <i className="fas fa-pen"></i> Edit
                    </button>
                  )}

                  {canManage && (
                    <button
                      className="eo-btn eo-btn-xs eo-btn-danger"
                      onClick={() => handleDelete(tmpl)}
                      disabled={actionInProgressId === tmpl.id}
                      title="Delete or archive template"
                    >
                      <i className="fas fa-trash-alt"></i> Delete
                    </button>
                  )}

                  {tmpl.status === 'approved' && onUseInSequence && (
                    <button
                      className="eo-btn eo-btn-xs eo-btn-success"
                      onClick={() => onUseInSequence(tmpl)}
                      title="Create sequence with this template"
                    >
                      <i className="fas fa-play"></i> Use in Sequence
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="eo-modal-overlay" role="dialog" aria-modal="true">
          <div className="eo-modal-container" style={{ maxWidth: '680px' }}>
            <div className="eo-modal-header">
              <div className="eo-modal-title-row">
                <h3>Template Preview: {previewTemplate.name}</h3>
                <span className="eo-badge-version">v{previewTemplate.version}</span>
              </div>
              <button className="eo-modal-close-btn" onClick={() => setPreviewTemplate(null)}>
                ×
              </button>
            </div>
            <div className="eo-modal-body">
              <div className="eo-preview-container">
                <div className="eo-preview-header">
                  <span style={{ fontSize: '12px', color: '#64748b' }}>SUBJECT:</span>
                  <div className="eo-preview-subject">
                    {previewTemplate.subject_template
                      .replace(/\{\{firstName\}\}/g, 'Sarah')
                      .replace(/\{\{companyName\}\}/g, 'Acme Corp')
                      .replace(/\{\{industry\}\}/g, 'Fintech')
                      .replace(/\{\{senderName\}\}/g, 'Alex Chen')}
                  </div>
                </div>
                <div className="eo-preview-body">
                  {previewTemplate.body_template
                    .replace(/\{\{firstName\}\}/g, 'Sarah')
                    .replace(/\{\{companyName\}\}/g, 'Acme Corp')
                    .replace(/\{\{industry\}\}/g, 'Fintech')
                    .replace(/\{\{senderName\}\}/g, 'Alex Chen')
                    .replace(/\{\{meetingUrl\}\}/g, 'https://calendar.app.google/demo')}
                </div>
                {previewTemplate.inherit_compliance_footer && complianceProfile && (
                  <div className="eo-preview-footer">
                    <div>
                      <strong>{complianceProfile.company_legal_name}</strong>{' '}
                      {complianceProfile.registration_number
                        ? `(${complianceProfile.registration_number})`
                        : ''}
                    </div>
                    <div>{complianceProfile.physical_address}</div>
                    <div style={{ marginTop: '4px', fontStyle: 'italic' }}>
                      {complianceProfile.unsubscribe_notice.replace(
                        '{{unsubscribeUrl}}',
                        'https://example.com/unsub'
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="eo-modal-footer">
              <button className="eo-btn eo-btn-secondary" onClick={() => setPreviewTemplate(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {editorOpen && (
        <div className="eo-modal-overlay" role="dialog" aria-modal="true">
          <div className="eo-modal-container" style={{ maxWidth: '780px' }}>
            <div className="eo-modal-header">
              <div className="eo-modal-title-row">
                <h3>
                  {editingTemplate
                    ? `Edit Template (v${editingTemplate.version})`
                    : 'New Governed Template'}
                </h3>
              </div>
              <button className="eo-modal-close-btn" onClick={() => setEditorOpen(false)}>
                ×
              </button>
            </div>
            <div className="eo-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="eo-field-group">
                  <label className="eo-field-label">Template Name *</label>
                  <input
                    type="text"
                    className="eo-field-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Q4 Executive Pitch"
                  />
                </div>
                <div className="eo-field-group">
                  <label className="eo-field-label">Category</label>
                  <select
                    className="eo-field-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORIES.filter((c) => c !== 'all').map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.replace(/_/g, ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Merge Tag Chips Palette */}
              <div>
                <label className="eo-field-label">Insert Merge Variable Tag:</label>
                <div className="eo-merge-tags-palette">
                  {MERGE_TAGS.map((m) => (
                    <button
                      key={m.tag}
                      type="button"
                      className="eo-merge-chip"
                      onClick={() => insertMergeTag(m.tag)}
                      title={m.desc}
                    >
                      + {m.tag}
                    </button>
                  ))}
                </div>
              </div>

              <div className="eo-field-group">
                <label className="eo-field-label">Subject Line Template *</label>
                <input
                  type="text"
                  className="eo-field-input"
                  value={formData.subjectTemplate}
                  onFocus={() => {
                    focusedFieldRef.current = 'subject';
                  }}
                  onChange={(e) => setFormData({ ...formData, subjectTemplate: e.target.value })}
                  placeholder="e.g. Scaling growth at {{companyName}} with AI"
                />
              </div>

              <div className="eo-field-group">
                <label className="eo-field-label">Email Body Template *</label>
                <textarea
                  className="eo-field-textarea"
                  rows={8}
                  value={formData.bodyTemplate}
                  onFocus={() => {
                    focusedFieldRef.current = 'body';
                  }}
                  onChange={(e) => setFormData({ ...formData, bodyTemplate: e.target.value })}
                  placeholder="Hi {{firstName}}, ..."
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  id="inheritFooter"
                  checked={formData.inheritComplianceFooter}
                  onChange={(e) =>
                    setFormData({ ...formData, inheritComplianceFooter: e.target.checked })
                  }
                />
                <label htmlFor="inheritFooter">
                  <strong>Enforce Workspace Legal Footer:</strong> Automatically appends corporate
                  address, registration no, and opt-out link.
                </label>
              </div>
            </div>
            <div className="eo-modal-footer">
              <button
                className="eo-btn eo-btn-secondary"
                onClick={() => setEditorOpen(false)}
                disabled={actionInProgressId === 'saving'}
              >
                Cancel
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="eo-btn eo-btn-secondary"
                  onClick={() => handleSaveTemplate(false)}
                  disabled={actionInProgressId === 'saving'}
                >
                  Save as Draft
                </button>
                <button
                  className="eo-btn eo-btn-primary"
                  onClick={() => handleSaveTemplate(true)}
                  disabled={actionInProgressId === 'saving'}
                >
                  Save & Submit for Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Data Profile Modal */}
      {complianceModalOpen && (
        <div className="eo-modal-overlay" role="dialog" aria-modal="true">
          <div className="eo-modal-container" style={{ maxWidth: '640px' }}>
            <div className="eo-modal-header">
              <div className="eo-modal-title-row">
                <h3>Edit Workspace Compliance Data</h3>
              </div>
              <button className="eo-modal-close-btn" onClick={() => setComplianceModalOpen(false)}>
                ×
              </button>
            </div>
            <div className="eo-modal-body">
              <div className="eo-field-group">
                <label className="eo-field-label">Company Legal Name *</label>
                <input
                  type="text"
                  className="eo-field-input"
                  value={compData.companyLegalName}
                  onChange={(e) => setCompData({ ...compData, companyLegalName: e.target.value })}
                  placeholder="e.g. Lead Huntrix Sdn Bhd"
                />
              </div>

              <div className="eo-field-group">
                <label className="eo-field-label">
                  Physical Street Address (Mandatory for Anti-Spam) *
                </label>
                <input
                  type="text"
                  className="eo-field-input"
                  value={compData.physicalAddress}
                  onChange={(e) => setCompData({ ...compData, physicalAddress: e.target.value })}
                  placeholder="e.g. Level 25, Menara Axiata, KL Sentral, Kuala Lumpur"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="eo-field-group">
                  <label className="eo-field-label">Business Registration Number</label>
                  <input
                    type="text"
                    className="eo-field-input"
                    value={compData.registrationNumber}
                    onChange={(e) =>
                      setCompData({ ...compData, registrationNumber: e.target.value })
                    }
                    placeholder="e.g. SSM 202401012345"
                  />
                </div>
                <div className="eo-field-group">
                  <label className="eo-field-label">Jurisdiction</label>
                  <input
                    type="text"
                    className="eo-field-input"
                    value={compData.jurisdiction}
                    onChange={(e) => setCompData({ ...compData, jurisdiction: e.target.value })}
                    placeholder="e.g. Malaysia (PDPA 2010)"
                  />
                </div>
              </div>

              <div className="eo-field-group">
                <label className="eo-field-label">Unsubscribe Notice Statement</label>
                <textarea
                  className="eo-field-textarea"
                  rows={2}
                  value={compData.unsubscribeNotice}
                  onChange={(e) => setCompData({ ...compData, unsubscribeNotice: e.target.value })}
                  placeholder="If you no longer wish to receive commercial emails from us, click here: {{unsubscribeUrl}}"
                />
              </div>

              <div className="eo-field-group">
                <label className="eo-field-label">Regulated-Market Disclaimer (Optional)</label>
                <textarea
                  className="eo-field-textarea"
                  rows={2}
                  value={compData.regulatedDisclaimer}
                  onChange={(e) =>
                    setCompData({ ...compData, regulatedDisclaimer: e.target.value })
                  }
                  placeholder="e.g. Regulated financial services notice, confidentiality disclosure..."
                />
              </div>
            </div>
            <div className="eo-modal-footer">
              <button
                className="eo-btn eo-btn-secondary"
                onClick={() => setComplianceModalOpen(false)}
                disabled={actionInProgressId === 'comp'}
              >
                Cancel
              </button>
              <button
                className="eo-btn eo-btn-primary"
                onClick={handleSaveCompliance}
                disabled={actionInProgressId === 'comp'}
              >
                Save Compliance Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplatesManagerTab;
